/**
 * Where a new thread does its work: the project folder, or a worktree cut for
 * it.
 *
 * Extracted from `Sidebar.logic`, which had grown past its cap holding every
 * decision the sidebar makes. This is one concern with two functions -- which
 * workspace a new thread starts in, and what it inherits from whatever is on
 * screen -- and it is now the concern the project settings page writes to, so
 * it earns its own module rather than a section of a grab-bag.
 */

export type SidebarNewThreadEnvMode = "local" | "worktree";

/**
 * Which workspace a new thread starts in, most specific source first.
 *
 * Three sources, and the order is the whole point. `requestedEnvMode` is what
 * someone picked for THIS thread, so it wins outright. `projectEnvMode` is what
 * the project pins for its own new threads — a default, not a lock, which is
 * why an explicit request still beats it. Everything else falls through to the
 * app-wide setting.
 *
 * `projectEnvMode` is nullable as well as optional because those mean different
 * things on the wire: `undefined` is a caller with no project in hand, `null` is
 * a project that has deliberately pinned nothing. Both fall through, but only
 * one of them can be sent by a server.
 */
export function resolveSidebarNewThreadEnvMode(input: {
  requestedEnvMode?: SidebarNewThreadEnvMode;
  projectEnvMode?: SidebarNewThreadEnvMode | null;
  defaultEnvMode: SidebarNewThreadEnvMode;
}): SidebarNewThreadEnvMode {
  return input.requestedEnvMode ?? input.projectEnvMode ?? input.defaultEnvMode;
}

export function resolveSidebarNewThreadSeedContext(input: {
  projectId: string;
  defaultEnvMode: SidebarNewThreadEnvMode;
  activeThread?: {
    projectId: string;
    branch: string | null;
    worktreePath: string | null;
  } | null;
  activeDraftThread?: {
    projectId: string;
    branch: string | null;
    worktreePath: string | null;
    envMode: SidebarNewThreadEnvMode;
  } | null;
}): {
  branch?: string | null;
  worktreePath?: string | null;
  envMode: SidebarNewThreadEnvMode;
} {
  if (input.defaultEnvMode === "worktree") {
    return {
      envMode: "worktree",
    };
  }

  if (input.activeDraftThread?.projectId === input.projectId) {
    return {
      branch: input.activeDraftThread.branch,
      worktreePath: input.activeDraftThread.worktreePath,
      envMode: input.activeDraftThread.envMode,
    };
  }

  if (input.activeThread?.projectId === input.projectId) {
    return {
      branch: input.activeThread.branch,
      worktreePath: input.activeThread.worktreePath,
      envMode: input.activeThread.worktreePath ? "worktree" : "local",
    };
  }

  return {
    envMode: input.defaultEnvMode,
  };
}
